<script setup>
import { useFileUpload } from '@/composables/useFileUpload'
import { useApi } from '@/utils/api'

definePage({
  meta: {
    layout: 'default',
    action: 'update',
    subject: 'Product',
  },
})

const router = useRouter()
const route = useRoute()
const productId = route.params.id

// ── State ──────────────────────────────────────────────────────────────────
const pageLoading = ref(true)
const loading = ref(false)
const errors = ref({})
const imageFile = ref(null)
const imagePreview = ref(null)
const snackbar = ref({ show: false, text: '', color: 'success' })
const categories = ref([])
const loadingCategories = ref(false)
const { uploading: uploadingImage, upload } = useFileUpload()

const form = ref({
  name: '',
  category_id: null,
  description: '',
  is_active: true,
  sort_order: 0,
  variants: [],
})

// ── Recipe dialog ──────────────────────────────────────────────────────────
const recipeDialog = ref(false)
const selectedVariant = ref(null)
const recipeLoading = ref(false)
const recipeSaving = ref(false)
const recipeItems = ref([])
const ingredients = ref([])

// ── Tier Grosir dialog (Addendum 10, Modul AO) ───────────────────────────────
const priceTierDialog = ref(false)
const priceTierVariant = ref(null)
const priceTierLoading = ref(false)
const priceTierSaving = ref(false)
const priceTierItems = ref([])

// ─ Init ───────────────────────────────────────────────────────────────────
const fetchCategories = async () => {
  loadingCategories.value = true
  try {
    const response = await useApi('/api/v1/categories', { query: { per_page: 100 } }).get()
    const data = response?.data ?? response

    categories.value = Array.isArray(data) ? data : (data?.data ?? [])
  } catch (e) {
    console.error('Failed to load categories:', e)
  } finally {
    loadingCategories.value = false
  }
}

const loadProduct = async () => {
  pageLoading.value = true
  try {
    const response = await useApi(`/api/v1/products/${productId}`).get()
    const data = response?.data ?? response
    const p = data?.data ?? data

    form.value = {
      name: p.name ?? '',
      category_id: p.category_id ?? null,
      description: p.description ?? '',
      is_active: p.is_active ?? true,
      sort_order: p.sort_order ?? 0,
      variants: (p.variants ?? []).map(v => ({
        id: v.id,
        name: v.name,
        sku: v.sku,
        barcode: v.barcode,
        price: v.price,
        is_active: v.is_active,
      })),
    }
    if (p.image_url) imagePreview.value = p.image_url
  } catch (err) {
    showSnackbar('Gagal memuat data produk', 'error')
  } finally {
    pageLoading.value = false
  }
}

onMounted(() => Promise.all([fetchCategories(), loadProduct()]))

// ── Variants ───────────────────────────────────────────────────────────────
const addVariant = () => {
  form.value.variants.push({ id: null, name: '', sku: '', barcode: '', price: 0, is_active: true })
}

// Varian existing (punya id) sengaja TIDAK bisa dihapus dari sini — mungkin
// sudah dipakai di order lama, riwayatnya harus tetap utuh. Nonaktifkan
// lewat toggle Aktif, bukan dihapus. Varian baru (belum disimpan, id null)
// aman dihapus dari form langsung.
const removeVariant = index => {
  form.value.variants.splice(index, 1)
}

// ── Image ──────────────────────────────────────────────────────────────────
const handleImageChange = file => {
  if (!file) {
    imagePreview.value = null
    imageFile.value = null
    return
  }
  imageFile.value = file
  imagePreview.value = URL.createObjectURL(file)
}

// ── Submit (info produk + semua varian sekaligus) ────────────────────────
const submit = async () => {
  loading.value = true
  errors.value = {}

  try {
    if (!form.value.variants || form.value.variants.length === 0) {
      errors.value.variants = ['Produk harus memiliki minimal 1 varian.']
      showSnackbar('Produk harus memiliki minimal 1 varian', 'warning')
      loading.value = false
      return
    }

    for (let i = 0; i < form.value.variants.length; i++) {
      const v = form.value.variants[i]
      if (!v.name || v.name.trim() === '') {
        errors.value[`variants.${i}.name`] = ['Nama varian wajib diisi']
        showSnackbar('Nama varian wajib diisi', 'warning')
        loading.value = false
        return
      }
      if (!v.price || v.price <= 0) {
        errors.value[`variants.${i}.price`] = ['Harga harus lebih dari 0']
        showSnackbar('Harga harus lebih dari 0', 'warning')
        loading.value = false
        return
      }
    }

    const payload = {
      name: form.value.name?.trim(),
      category_id: form.value.category_id,
      description: form.value.description?.trim() || null,
      is_active: form.value.is_active,
      sort_order: form.value.sort_order || 0,
      variants: form.value.variants.map(v => ({
        id: v.id || undefined,
        name: v.name?.trim(),
        sku: v.sku?.trim() || null,
        barcode: v.barcode?.trim() || null,
        price: parseFloat(v.price) || 0,
        is_active: v.is_active !== undefined ? v.is_active : true,
      })),
    }

    await useApi(`/api/v1/products/${productId}`).put({ body: payload })

    if (imageFile.value) {
      const { error: uploadError } = await upload(
        imageFile.value,
        `/api/v1/products/${productId}/image`,
        'image'
      )

      if (uploadError) {
        console.warn('Image upload failed:', uploadError)
        showSnackbar('Produk diperbarui, tapi upload gambar gagal', 'warning')
        loading.value = false
        return
      }
    }

    showSnackbar('Produk berhasil diperbarui')
    router.push({ name: 'catalog-products' })
  } catch (err) {
    if (err?.status === 422 || err?.response?.status === 422) {
      errors.value = err?.data?.errors ?? err?.response?.data?.errors ?? {}
      showSnackbar(err?.data?.message ?? 'Periksa kembali isian form', 'warning')
    } else {
      showSnackbar(err?.data?.message ?? err?.response?.data?.message ?? 'Terjadi kesalahan', 'error')
    }
  } finally {
    loading.value = false
  }
}

// ── Recipe ─────────────────────────────────────────────────────────────────
const openRecipe = async variant => {
  if (!variant.id) {
    showSnackbar('Simpan varian ini dulu sebelum atur recipe-nya', 'warning')
    return
  }

  selectedVariant.value = variant
  recipeDialog.value = true
  recipeLoading.value = true
  recipeItems.value = []

  try {
    const [recipeRes, ingRes] = await Promise.all([
      useApi(`/api/v1/variants/${variant.id}/recipe`).get(),
      useApi('/api/v1/ingredients', { query: { per_page: 200 } }).get(),
    ])

    const recipeData = recipeRes?.data ?? recipeRes
    const ingData = ingRes?.data ?? ingRes

    recipeItems.value = (recipeData?.recipes ?? []).map(i => ({
      ingredient_id: i.ingredient?.id,
      ingredient_name: i.ingredient?.name,
      quantity: i.quantity,
      unit_id: i.unit?.id ?? null,
      unit_name: i.unit?.name ?? '',
    }))

    ingredients.value = Array.isArray(ingData) ? ingData : (ingData?.data ?? [])
  } catch (err) {
    console.error('Failed to load recipe:', err)
    showSnackbar('Gagal memuat recipe', 'error')
  } finally {
    recipeLoading.value = false
  }
}

const addRecipeItem = () => {
  recipeItems.value.push({ ingredient_id: null, quantity: 1, unit_id: null, unit_name: '' })
}
const removeRecipeItem = idx => {
  recipeItems.value.splice(idx, 1)
}

const onIngredientSelect = (idx, ingredientId) => {
  const ing = ingredients.value.find(i => i.id === ingredientId)
  if (ing) {
    recipeItems.value[idx].unit_id = ing.unit?.id ?? null
    recipeItems.value[idx].unit_name = ing.unit?.name ?? ''
  }
}

const saveRecipe = async () => {
  recipeSaving.value = true
  try {
    const items = recipeItems.value
      .filter(i => i.ingredient_id && i.unit_id)
      .map(i => ({
        ingredient_id: i.ingredient_id,
        unit_id: i.unit_id,
        quantity: Number(i.quantity),
      }))

    await useApi(`/api/v1/variants/${selectedVariant.value.id}/recipe`).put({
      body: { items },
    })

    showSnackbar('Recipe berhasil disimpan')
    recipeDialog.value = false
  } catch (err) {
    console.error('Save recipe error:', err)
    showSnackbar(err?.data?.message ?? 'Gagal menyimpan recipe', 'error')
  } finally {
    recipeSaving.value = false
  }
}

// ── Tier Grosir (Addendum 10, Modul AO) ─────────────────────────────────────
// Sama pola persis dengan Recipe di atas (mirror by design, bukan diextract
// jadi shared composable -- Recipe sendiri sudah tidak diextract meski
// diduplikasi persis di halaman detail produk, [id]/index.vue).
const openPriceTiers = async variant => {
  if (!variant.id) {
    showSnackbar('Simpan varian ini dulu sebelum atur tier grosirnya', 'warning')
    return
  }

  priceTierVariant.value = variant
  priceTierDialog.value = true
  priceTierLoading.value = true
  priceTierItems.value = []

  try {
    const response = await useApi(`/api/v1/variants/${variant.id}/price-tiers`).get()
    const data = response?.data ?? response

    priceTierItems.value = (data?.tiers ?? []).map(t => ({
      min_quantity: t.min_quantity,
      price_per_unit: t.price_per_unit,
    }))
  } catch (err) {
    console.error('Failed to load price tiers:', err)
    showSnackbar('Gagal memuat tier grosir', 'error')
  } finally {
    priceTierLoading.value = false
  }
}

const addPriceTierItem = () => {
  priceTierItems.value.push({ min_quantity: null, price_per_unit: null })
}
const removePriceTierItem = idx => {
  priceTierItems.value.splice(idx, 1)
}

const savePriceTiers = async () => {
  priceTierSaving.value = true
  try {
    // Urut otomatis by min_quantity sebelum dikirim, sesuai spek addendum
    // §4.2 -- backend juga selalu balas terurut (PriceTierController::show/sync),
    // ini sekadar memastikan urutan konsisten di form kalau user isi acak.
    const items = priceTierItems.value
      .filter(t => t.min_quantity && t.price_per_unit !== null && t.price_per_unit !== '')
      .map(t => ({ min_quantity: Number(t.min_quantity), price_per_unit: Number(t.price_per_unit) }))
      .sort((a, b) => a.min_quantity - b.min_quantity)

    await useApi(`/api/v1/variants/${priceTierVariant.value.id}/price-tiers`).put({
      body: { items },
    })

    showSnackbar('Tier grosir berhasil disimpan')
    priceTierDialog.value = false
  } catch (err) {
    console.error('Save price tiers error:', err)
    showSnackbar(err?.data?.message ?? 'Gagal menyimpan tier grosir', 'error')
  } finally {
    priceTierSaving.value = false
  }
}

const showSnackbar = (text, color = 'success') => {
  snackbar.value = { show: true, text, color }
}

const breadcrumbs = [
  { title: 'Katalog', disabled: true },
  { title: 'Produk', to: { name: 'catalog-products' } },
  { title: 'Edit Produk', disabled: true },
]
</script>

<template>
  <div>
    <VBreadcrumbs :items="breadcrumbs" class="px-0 mb-4" />

    <div v-if="pageLoading" class="d-flex justify-center py-10">
      <VProgressCircular indeterminate color="primary" />
    </div>

    <template v-else>
      <VRow>
        <!-- Left: Main form -->
        <VCol cols="12" md="8">
          <!-- Basic info -->
          <VCard class="mb-4">
            <VCardTitle class="pa-5 pb-0">Informasi Produk</VCardTitle>
            <VCardText class="pt-4">
              <VRow dense>
                <VCol cols="12">
                  <VTextField
                    v-model="form.name"
                    label="Nama Produk *"
                    placeholder="contoh: Kopi Susu"
                    :error-messages="errors['name']"
                  />
                </VCol>
                <VCol cols="12" sm="6">
                  <VSelect
                    v-model="form.category_id"
                    :items="categories"
                    item-title="name"
                    item-value="id"
                    label="Kategori"
                    clearable
                    :loading="loadingCategories"
                    :error-messages="errors['category_id']"
                  />
                </VCol>
                <VCol cols="12" sm="6">
                  <VTextField
                    v-model.number="form.sort_order"
                    label="Urutan Tampil"
                    type="number"
                    min="0"
                    :error-messages="errors['sort_order']"
                  />
                </VCol>
                <VCol cols="12">
                  <VTextarea
                    v-model="form.description"
                    label="Deskripsi"
                    rows="3"
                    :error-messages="errors['description']"
                  />
                </VCol>
              </VRow>
            </VCardText>
          </VCard>

          <!-- Variants -->
          <VCard>
            <VCardTitle class="pa-5 pb-0 d-flex align-center justify-space-between">
              <span>Varian &amp; Harga</span>
              <VBtn
                size="small"
                variant="tonal"
                prepend-icon="ri-add-line"
                @click="addVariant"
              >
                Tambah Varian
              </VBtn>
            </VCardTitle>
            <VCardText class="pt-4">
              <VAlert
                v-if="errors['variants']"
                type="error"
                variant="tonal"
                class="mb-4"
              >
                {{ errors['variants'][0] }}
              </VAlert>

              <div
                v-for="(variant, idx) in form.variants"
                :key="variant.id ?? `new-${idx}`"
              >
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="d-flex align-center gap-2">
                    <span class="text-body-2 font-weight-medium text-medium-emphasis">
                      Varian {{ idx + 1 }}
                    </span>
                    <VChip v-if="!variant.id" size="x-small" color="info" variant="tonal">
                      Baru
                    </VChip>
                  </div>

                  <div class="d-flex align-center gap-3">
                    <VBtn
                      variant="text"
                      size="x-small"
                      prepend-icon="ri-restaurant-line"
                      @click="openRecipe(variant)"
                    >
                      Recipe
                    </VBtn>
                    <VBtn
                      variant="text"
                      size="x-small"
                      prepend-icon="ri-price-tag-3-line"
                      @click="openPriceTiers(variant)"
                    >
                      Tier Grosir
                    </VBtn>
                    <div v-if="variant.id" class="d-flex align-center gap-1">
                      <span class="text-caption text-medium-emphasis">Aktif</span>
                      <VSwitch v-model="variant.is_active" hide-details density="compact" color="success" />
                    </div>
                    <VBtn
                      v-else
                      icon
                      variant="text"
                      size="x-small"
                      color="error"
                      @click="removeVariant(idx)"
                    >
                      <VIcon icon="ri-delete-bin-line" />
                    </VBtn>
                  </div>
                </div>

                <VRow dense>
                  <VCol cols="12" sm="5">
                    <VTextField
                      v-model="variant.name"
                      label="Nama Varian *"
                      placeholder="Regular / Large"
                      :error-messages="errors[`variants.${idx}.name`]"
                    />
                  </VCol>
                  <VCol cols="6" sm="3">
                    <VTextField
                      v-model="variant.sku"
                      label="SKU"
                      :error-messages="errors[`variants.${idx}.sku`]"
                    />
                  </VCol>
                  <VCol cols="6" sm="4">
                    <VTextField
                      v-model.number="variant.price"
                      label="Harga *"
                      type="number"
                      min="0"
                      prefix="Rp "
                      :error-messages="errors[`variants.${idx}.price`]"
                    />
                  </VCol>
                  <VCol cols="12" sm="6">
                    <VTextField
                      v-model="variant.barcode"
                      label="Barcode (opsional)"
                      placeholder="Scan atau ketik manual"
                      prepend-inner-icon="ri-barcode-line"
                      :error-messages="errors[`variants.${idx}.barcode`]"
                      hint="Dipakai untuk scan cepat di POS (fitur Retail)"
                      persistent-hint
                    />
                  </VCol>
                </VRow>

                <VDivider v-if="idx < form.variants.length - 1" class="my-3" />
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <!-- Right: image + status -->
        <VCol cols="12" md="4">
          <VCard class="mb-4">
            <VCardTitle class="pa-5 pb-0">Foto Produk</VCardTitle>
            <VCardText class="pt-4">
              <div
                class="image-upload-area rounded-lg d-flex flex-column align-center justify-center pa-4 mb-3"
                :class="imagePreview ? '' : 'border-dashed'"
              >
                <VImg
                  v-if="imagePreview"
                  :src="imagePreview"
                  height="160"
                  cover
                  rounded="lg"
                />
                <div v-else class="text-center">
                  <VIcon icon="ri-image-add-line" size="40" color="medium-emphasis" />
                  <p class="text-caption text-medium-emphasis mt-2 mb-0">Klik untuk unggah foto</p>
                </div>
              </div>
              <VFileInput
                label="Pilih Foto"
                accept="image/*"
                hide-details
                prepend-icon=""
                prepend-inner-icon="ri-upload-line"
                variant="outlined"
                :loading="uploadingImage"
                @update:model-value="handleImageChange"
              />
              <p class="text-caption text-medium-emphasis mt-1">Maks. 2MB · JPG, PNG</p>
            </VCardText>
          </VCard>

          <VCard>
            <VCardTitle class="pa-5 pb-0">Status</VCardTitle>
            <VCardText class="pt-4">
              <div class="d-flex align-center justify-space-between">
                <span class="text-body-2">Produk Aktif</span>
                <VSwitch v-model="form.is_active" hide-details color="success" />
              </div>
              <p class="text-caption text-medium-emphasis mt-1">
                Produk non-aktif tidak muncul di POS
              </p>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>

      <!-- Footer actions -->
      <VRow class="mt-2">
        <VCol cols="12" class="d-flex gap-3 justify-end">
          <VBtn variant="tonal" :to="{ name: 'catalog-products' }">Batal</VBtn>
          <VBtn :loading="loading || uploadingImage" @click="submit">Simpan Perubahan</VBtn>
        </VCol>
      </VRow>
    </template>

    <!-- ── Dialog: Recipe ─────────────────────────────────────────────── -->
    <VDialog v-model="recipeDialog" max-width="640">
      <VCard>
        <VCardTitle class="pa-5 pb-0 d-flex align-center gap-2">
          Recipe — {{ selectedVariant?.name }}
          <VChip size="small" color="info" variant="tonal">BOM</VChip>
        </VCardTitle>
        <VCardText class="pt-4">
          <div v-if="recipeLoading" class="text-center py-6">
            <VProgressCircular indeterminate color="primary" />
          </div>

          <template v-else>
            <div v-if="!recipeItems.length" class="text-center py-6 mb-3">
              <VIcon icon="ri-restaurant-line" size="48" color="medium-emphasis" class="mb-2" />
              <p class="text-body-2 text-medium-emphasis">
                Belum ada bahan. Tambah untuk menghitung estimasi HPP.
              </p>
            </div>

            <div
              v-for="(item, idx) in recipeItems"
              :key="idx"
              class="mb-3"
            >
              <VRow dense align="center">
                <VCol cols="6">
                  <VAutocomplete
                    v-model="item.ingredient_id"
                    :items="ingredients"
                    item-title="name"
                    item-value="id"
                    label="Bahan *"
                    hide-details
                    @update:model-value="v => onIngredientSelect(idx, v)"
                  />
                </VCol>
                <VCol cols="4">
                  <VTextField
                    v-model.number="item.quantity"
                    label="Jumlah *"
                    type="number"
                    min="0"
                    step="0.01"
                    hide-details
                    :suffix="item.unit_name"
                  />
                </VCol>
                <VCol cols="2" class="text-right">
                  <VBtn
                    icon
                    variant="text"
                    size="small"
                    color="error"
                    @click="removeRecipeItem(idx)"
                  >
                    <VIcon icon="ri-delete-bin-line" />
                  </VBtn>
                </VCol>
              </VRow>
            </div>

            <VBtn
              variant="tonal"
              prepend-icon="ri-add-line"
              size="small"
              class="mt-2"
              @click="addRecipeItem"
            >
              Tambah Bahan
            </VBtn>
          </template>
        </VCardText>
        <VCardActions class="justify-end pa-5 pt-0 gap-3">
          <VBtn variant="tonal" @click="recipeDialog = false">Tutup</VBtn>
          <VBtn :loading="recipeSaving" @click="saveRecipe">Simpan Recipe</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- ── Dialog: Tier Grosir (Addendum 10, Modul AO) ──────────────────── -->
    <VDialog v-model="priceTierDialog" max-width="560">
      <VCard>
        <VCardTitle class="pa-5 pb-0 d-flex align-center gap-2">
          Tier Grosir — {{ priceTierVariant?.name }}
          <VChip size="small" color="info" variant="tonal">Retail</VChip>
        </VCardTitle>
        <VCardText class="pt-4">
          <div v-if="priceTierLoading" class="text-center py-6">
            <VProgressCircular indeterminate color="primary" />
          </div>

          <template v-else>
            <VAlert type="info" variant="tonal" density="compact" class="mb-4">
              Beli minimal sejumlah qty tertentu → harga per unit otomatis
              turun ke tier itu. Kosongkan semua tier untuk kembali ke harga
              varian biasa ({{ formatRupiah(priceTierVariant?.price) }}).
            </VAlert>

            <div v-if="!priceTierItems.length" class="text-center py-6 mb-3">
              <VIcon icon="ri-price-tag-3-line" size="48" color="medium-emphasis" class="mb-2" />
              <p class="text-body-2 text-medium-emphasis">
                Belum ada tier grosir. Harga tetap pakai harga varian biasa.
              </p>
            </div>

            <div
              v-for="(tier, idx) in priceTierItems"
              :key="idx"
              class="mb-3"
            >
              <VRow dense align="center">
                <VCol cols="5">
                  <VTextField
                    v-model.number="tier.min_quantity"
                    label="Minimal Qty *"
                    type="number"
                    min="1"
                    hide-details
                  />
                </VCol>
                <VCol cols="5">
                  <VTextField
                    v-model.number="tier.price_per_unit"
                    label="Harga per Unit *"
                    type="number"
                    min="0"
                    prefix="Rp "
                    hide-details
                  />
                </VCol>
                <VCol cols="2" class="text-right">
                  <VBtn
                    icon
                    variant="text"
                    size="small"
                    color="error"
                    @click="removePriceTierItem(idx)"
                  >
                    <VIcon icon="ri-delete-bin-line" />
                  </VBtn>
                </VCol>
              </VRow>
            </div>

            <VBtn
              variant="tonal"
              prepend-icon="ri-add-line"
              size="small"
              class="mt-2"
              @click="addPriceTierItem"
            >
              Tambah Tier
            </VBtn>
          </template>
        </VCardText>
        <VCardActions class="justify-end pa-5 pt-0 gap-3">
          <VBtn variant="tonal" @click="priceTierDialog = false">Tutup</VBtn>
          <VBtn :loading="priceTierSaving" @click="savePriceTiers">Simpan Tier Grosir</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VSnackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
    </VSnackbar>
  </div>
</template>

<style scoped>
.image-upload-area {
  min-height: 160px;
  background: rgba(var(--v-theme-on-surface), 0.04);
  cursor: pointer;
  transition: background 0.2s;
}
.image-upload-area:hover {
  background: rgba(var(--v-theme-primary), 0.08);
}
.border-dashed {
  border: 2px dashed rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>